// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@rpsc/App/Components/Common/XngZone";
import XngSticky from "@rpsc/App/Components/XngSticky";
import React from "react";
import { css } from "styled-components";

type Props = WidgetProps;

export interface OnePageCheckoutPageContainerStyles {
    container?: GridContainerProps;
    leftGridItem?: GridItemProps;
    spaceGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    // XNG-Change
    backToCartLink?: LinkPresentationProps;
    leftSection?: InjectableCss;
    title?: TypographyPresentationProps;
    leftItemsSection?: InjectableCss;
    addressesContainer?: InjectableCss;
    shippingContainer?: InjectableCss;
    paymentContainer?: InjectableCss;
    productsContainer?: InjectableCss;
    rightSection?: InjectableCss;
    wrapper?: InjectableCss;
}

export const containerStyles: OnePageCheckoutPageContainerStyles = {
    leftGridItem: {
        width: [12, 12, 6, 7, 7],
        css: css`
            & > div > div:not(:last-child) {
                padding-bottom: 30px;
                margin-bottom: 30px;
                border-bottom: 1px solid ${getColor("common.border")};
            }
        `,
    },
    spaceGridItem: { width: [0, 0, 1, 1, 1] },
    rightGridItem: { width: [12, 12, 5, 4, 4] },
};

const styles = containerStyles;

const OnePageCheckoutPageContainer = ({ id }: Props) => {
    const cartUrl = useAppSelector(state => getPageLinkByPageType(state, "CartPage")?.url);
    return (
        <StyledWrapper {...styles.wrapper}>
            <Link href={cartUrl} {...styles.backToCartLink} aria-label={translate("Back to cart")}>
                {translate("Back to cart")}
            </Link>
            <StyledWrapper {...styles.container} data-test-selectors="CheckoutPageContainer">
                <StyledWrapper {...styles.leftSection}>
                    {/*  XNG-Change */}
                    <Typography {...styles.title} data-test-selector="PageTitle">
                        {translate("Checkout")}
                    </Typography>
                    <StyledWrapper {...styles.leftItemsSection}>
                        <StyledWrapper {...styles.addressesContainer}>
                            <XngZone
                                zoneName="Content0"
                                contentId={id}
                                hint={translate("Shipping Address")}
                                normalWidth={true}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.shippingContainer}>
                            <XngZone zoneName="Content3" contentId={id} hint={translate("Delivery Method")} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.paymentContainer}>
                            <XngZone zoneName="Content4" contentId={id} hint={translate("Payment")} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.productsContainer}>
                            <XngZone zoneName="Content5" contentId={id} hint={translate("Products")} />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <XngSticky>
                        <XngZone zoneName="Content1" contentId={id} hint={translate("Total & Promotions")} />
                    </XngSticky>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OnePageCheckoutPageContainer,
    definition: {
        displayName: "Page Container",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
